import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { useAuth } from './AuthContext';
import { debounce } from '../utils/debounce';

const logger = createLogger({ module: 'TutorialContext', level: 'info' });

// Public routes that should not trigger tutorial checks
const PUBLIC_ROUTES = ['', '/', '/landing', '/login'];

// Constants for error handling
const RETRY_DELAY = 3000; // 3 seconds
const MAX_RETRIES = 2;

// Type for debounced function that can be canceled
interface DebouncedFunction {
  (pageKey: string): void;
  cancel?: () => void;
}

interface TutorialSection {
  title: string;
  content: string;
  image?: string;
}

interface PageTutorial {
  id: string;
  page_key: string;
  title: string;
  content: {
    sections: TutorialSection[];
    version: number;
  };
  created_at: string;
  updated_at: string;
}

interface TutorialContextType {
  isOpen: boolean;
  openTutorial: () => void;
  closeTutorial: () => void;
  currentTutorial: PageTutorial | null;
  currentSection: number;
  totalSections: number;
  nextSection: () => void;
  previousSection: () => void;
  goToSection: (index: number) => void;
  loading: boolean;
  error: Error | null;
  hasTutorial: boolean;
  checkTutorialExists: (pageKey: string) => Promise<boolean>;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState<PageTutorial | null>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasTutorial, setHasTutorial] = useState(false);
  
  const location = useLocation();
  const { user } = useAuth();
  const retryCount = useRef(0);
  const lastSuccessfulCheck = useRef<string | null>(null);
  
  // Store current page key in a ref to avoid unnecessary re-renders
  const currentPageKeyRef = useRef<string>('');
  
  // Track active operations to prevent duplicate requests
  const pendingOperationsRef = useRef({
    checkingTutorial: false,
    fetchingTutorial: false
  });
  
  // Timeout ref to clear on unmount
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to get page key from pathname
  const getPageKey = useCallback((pathname: string): string => {
    // Remove trailing slash if present
    const cleanPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    
    // Split the path
    const segments = cleanPath.split('/');
    
    // If it's the root path or empty, return 'home'
    if (segments.length <= 1) {
      return 'home';
    }
    
    // Otherwise return the first path segment
    return segments[1].toLowerCase();
  }, []);

  const checkTutorialExists = useCallback(async (pageKey: string): Promise<boolean> => {
    // Skip tutorial checks for public routes or when user is not authenticated
    if (!user || PUBLIC_ROUTES.includes(location.pathname)) {
      return false;
    }

    // If we've already successfully checked this page and have a result, use it
    if (lastSuccessfulCheck.current === pageKey) {
      return hasTutorial;
    }
    
    // If there's already a pending check, don't start another one
    if (pendingOperationsRef.current.checkingTutorial) {
      return hasTutorial;
    }
    
    pendingOperationsRef.current.checkingTutorial = true;
    
    try {
      const { data, error: fetchError } = await supabase
        .from('page_tutorials')
        .select('id')
        .eq('page_key', pageKey)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      // Reset retry count on success
      retryCount.current = 0;
      lastSuccessfulCheck.current = pageKey;
      
      const exists = !!data;
      
      // Only update state if necessary
      if (exists !== hasTutorial) {
        setHasTutorial(exists);
      }
      
      return exists;
    } catch (err) {
      const isNetworkError = err instanceof Error && 
        (err.message.includes('Failed to fetch') || err.message.includes('Network Error'));

      if (isNetworkError && retryCount.current < MAX_RETRIES) {
        logger.warn('Network error checking tutorial existence, will retry:', {
          pageKey,
          retryCount: retryCount.current + 1,
          error: err
        });
        
        retryCount.current++;
        
        // Return last known state for this page if available
        if (lastSuccessfulCheck.current === pageKey) {
          return hasTutorial;
        }
        
        // Otherwise, schedule a retry
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          const currentPageKey = getPageKey(location.pathname);
          if (currentPageKey === pageKey) {
            pendingOperationsRef.current.checkingTutorial = false;
            checkTutorialExists(pageKey).then(setHasTutorial);
          }
        }, RETRY_DELAY);
        
        return false;
      }

      logger.error('Error checking tutorial existence:', err);
      return false;
    } finally {
      pendingOperationsRef.current.checkingTutorial = false;
    }
  }, [user, location.pathname, hasTutorial, getPageKey]);

  // Debounced version of checkTutorialExists to prevent rapid-fire requests
  const debouncedCheck = useMemo(() => {
    const fn = debounce((pageKey: string) => {
      if (pendingOperationsRef.current.checkingTutorial) return;
      
      checkTutorialExists(pageKey).then(exists => {
        if (exists !== hasTutorial) {
          setHasTutorial(exists);
        }
      });
    }, 300) as DebouncedFunction;
    
    return fn;
  }, [checkTutorialExists, hasTutorial]);

  // Move the fetchTutorial function definition before the useEffect hook that uses it
  // Update the fetchTutorial function to be more reliable
  const fetchTutorial = useCallback(async (pageKey: string) => {
    // Skip tutorial fetching for public routes or when user is not authenticated
    if (!user || PUBLIC_ROUTES.includes(location.pathname)) {
      setCurrentTutorial(null);
      setLoading(false);
      return;
    }
    
    // Skip if already fetching 
    if (pendingOperationsRef.current.fetchingTutorial) {
      return;
    }
    
    pendingOperationsRef.current.fetchingTutorial = true;
    
    try {
      setLoading(true);
      setError(null);

      logger.debug('Fetching tutorial for page:', pageKey);
      const { data, error: fetchError } = await supabase
        .from('page_tutorials')
        .select('*')
        .eq('page_key', pageKey)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        logger.debug('Tutorial fetched successfully:', { pageKey, title: data.title });
        setCurrentTutorial(data);
        setCurrentSection(0);
      } else {
        logger.debug('No tutorial found for page:', pageKey);
        setCurrentTutorial(null);
      }
    } catch (err) {
      logger.error('Error fetching tutorial:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch tutorial'));
      setCurrentTutorial(null);
    } finally {
      setLoading(false);
      pendingOperationsRef.current.fetchingTutorial = false;
    }
  }, [user, location.pathname]);

  // Update the effect that watches for location changes
  useEffect(() => {
    // Skip tutorial checks for public routes or when user is not authenticated
    if (!user || PUBLIC_ROUTES.includes(location.pathname)) {
      setHasTutorial(false);
      setCurrentTutorial(null);
      return;
    }
    
    const pageKey = getPageKey(location.pathname);
    
    // Reset the current tutorial if the page has changed
    if (currentPageKeyRef.current !== pageKey) {
      setCurrentTutorial(null);
      setCurrentSection(0);
      
      // If the tutorial is open, fetch the new tutorial immediately
      if (isOpen) {
        fetchTutorial(pageKey);
      }
    }
    
    currentPageKeyRef.current = pageKey;
    
    if (lastSuccessfulCheck.current === pageKey) {
      // We've already checked this page, no need to do it again
      return;
    }
    
    debouncedCheck(pageKey);
    
    return () => {
      if (debouncedCheck && typeof debouncedCheck.cancel === 'function') {
        try {
          debouncedCheck.cancel();
        } catch (err) {
          // Just log the error but don't crash if cancel isn't available
          logger.error('Error canceling debounced check:', err);
        }
      }
    };
  }, [location.pathname, debouncedCheck, getPageKey, user, isOpen, fetchTutorial]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (debouncedCheck && typeof debouncedCheck.cancel === 'function') {
        try {
          debouncedCheck.cancel();
        } catch (err) {
          // Just log the error but don't crash if cancel isn't available
          logger.error('Error canceling debounced check on unmount:', err);
        }
      }
    };
  }, [debouncedCheck]);

  // Remove the previous openTutorial effect as it's now redundant
  // Keep the openTutorial function simple
  const openTutorial = useCallback(() => {
    // Skip opening tutorial for public routes or when user is not authenticated
    if (!user || PUBLIC_ROUTES.includes(location.pathname)) {
      return;
    }
    
    const pageKey = getPageKey(location.pathname);
    setIsOpen(true);
    
    // Always fetch the tutorial for the current page when opening
    fetchTutorial(pageKey);
  }, [location.pathname, fetchTutorial, getPageKey, user]);

  const closeTutorial = useCallback(() => {
    setIsOpen(false);
    setCurrentSection(0);
  }, []);

  const totalSections = useMemo(() => {
    return currentTutorial?.content?.sections?.length || 0;
  }, [currentTutorial]);

  const nextSection = useCallback(() => {
    if (currentSection < totalSections - 1) {
      setCurrentSection(prev => prev + 1);
    }
  }, [currentSection, totalSections]);

  const previousSection = useCallback(() => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  }, [currentSection]);

  const goToSection = useCallback((index: number) => {
    if (index >= 0 && index < totalSections) {
      setCurrentSection(index);
    }
  }, [totalSections]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    isOpen,
    openTutorial,
    closeTutorial,
    currentTutorial,
    currentSection,
    totalSections,
    nextSection,
    previousSection,
    goToSection,
    loading,
    error,
    hasTutorial,
    checkTutorialExists
  }), [
    isOpen,
    openTutorial,
    closeTutorial,
    currentTutorial,
    currentSection,
    totalSections,
    nextSection,
    previousSection,
    goToSection,
    loading,
    error,
    hasTutorial,
    checkTutorialExists
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export { TutorialProvider, useTutorial };
export type { TutorialContextType, PageTutorial, TutorialSection }; 